import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ExportArtifactType, ResumeDocument } from '../../lib/api';
import {
  type ApiError,
  driveAuthorizeUrl,
  driveExport,
  driveStatus,
  getBillingEntitlement,
} from '../../lib/api';
import {
  type ExportDestination,
  type ExportFlowPhase,
  type ExportFormat,
  defaultDestination,
  driveDestinationAvailable,
  isExportFlowBusy,
  shouldPersistSelection,
} from '../../lib/exportFlowState';
import { classifyDriveError, type ClassifiedDriveError } from '../../lib/driveExportState';
import { buildPrintDocumentTitle, type PrintArtifact } from '../../lib/printDocumentTitle';
import {
  DEFAULT_ACCENT_KEY,
  RESUME_ACCENT_OPTIONS,
  RESUME_TEMPLATE_META_BY_FAMILY,
  isBuiltFamily,
  isPremiumFamily,
  orderedTemplateMeta,
  type ResumeTemplateFamily,
} from '../../lib/resumeTemplateRegistry';
import { downloadResumeDocx } from '../../lib/resumeToDocx';
import styles from './ExportFlow.module.css';

// =============================================================================
// ExportFlow — the owner's verbatim ruling (2026-07-06): "We don't need to
// see the resume templates until we want to export, which should be a
// different workflow." docs/design/export-workflow-2026-07-06.md.
//
// This is THE export workflow: an explicit Export trigger (mounted where the
// old inline TemplatePicker + Download PDF/.docx/Drive buttons used to live
// in CorpusResumePanel/JobResumePanel) opens this focused overlay — template
// family + accent (the SAME shared registry data + free/premium gating
// TemplatePicker already enforced, untouched), format (read-only, derived
// from the served doc.formatVariant — never a user-editable axis; Letter/A4
// is a LOCALE property of the document, not an export preference), and
// destination (download vs Save to Drive), plus a live filename preview.
//
// HOUSE SHAPE: overlay/panel structure mirrors GapAddressPanel.tsx (a
// backdrop-dismiss dialog sliding up from the bottom); the dependency-free
// focus trap + Escape-close + aria-hidden/tabIndex gating mirrors
// SideNavDrawer.tsx's own established pattern verbatim (no new focus-trap
// dependency, matching that file's own documented "no new deps" discipline).
//
// THIN PRESENTATION / NO INVENTION: every family/accent/premium-gate/
// filename-segment rendered here comes from the shared registry, the
// server's own entitlement check (billing/entitlement — a client-side HINT
// only; the real gate is always exports.routes.ts's server-side check, same
// as TemplatePicker's own documented posture), or values the caller already
// has served (doc, resumeId, applicantName, roleTitle, company). This
// component invents nothing; it only sequences an existing preference PATCH,
// an existing PDF/docx client render, and the existing Drive POST.
// =============================================================================

export interface ExportFlowProps {
  open: boolean;
  onClose: () => void;
  doc: ResumeDocument;
  /** The persisted family/accent (useTemplatePreference) — pre-selects the
   *  flow's pickers exactly as the reading-flow's old inline TemplatePicker
   *  used to. Changing a picker inside the flow calls `onSelectFamily`/
   *  `onSelectAccent`, the SAME setters useTemplatePreference already
   *  returns (the existing PATCH /account/template-preference endpoint —
   *  no new endpoint). */
  family: string;
  accentKey: string;
  onSelectFamily: (family: ResumeTemplateFamily) => void;
  onSelectAccent: (accentKey: string) => void;
  /** The artifact this export targets — present once the underlying
   *  résumé/version actually exists server-side (matches every existing
   *  DriveSaveButton mount site's own "only render once there's a real id"
   *  rule). Absent (null) simply hides the Drive destination — download
   *  stays available either way, since the PDF/docx render is entirely
   *  client-side over the already-served `doc`. */
  artifactId: string | null;
  artifactType: ExportArtifactType;
  /** Naming inputs — identical to what CorpusResumePanel/JobResumePanel
   *  already pass to buildPrintDocumentTitle today; this component performs
   *  no derivation of its own beyond what that shared house convention
   *  already does. */
  displayName?: string | null;
  roleTitle?: string | null;
  company?: string | null;
  artifactLabel: PrintArtifact;
}

/** aria-expanded trigger button — mounted at the toolbar site the old
 *  Download PDF/.docx/Print/Drive row used to occupy. A single, explicit
 *  Export action opens the focused flow below; Print from browser stays a
 *  SEPARATE, labeled convenience beside this trigger (R275's own framing —
 *  never promoted into this flow, never removed). */
export function ExportTriggerButton({
  onClick,
  open,
  className,
}: {
  onClick: () => void;
  open: boolean;
  className: string;
}) {
  return (
    <button type="button" className={className} onClick={onClick} aria-expanded={open} aria-haspopup="dialog">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3v12m0 0-4-4m4 4 4-4M5 19h14"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      Export
    </button>
  );
}

export function ExportFlow({
  open,
  onClose,
  doc,
  family,
  accentKey,
  onSelectFamily,
  onSelectAccent,
  artifactId,
  artifactType,
  displayName,
  roleTitle,
  company,
  artifactLabel,
}: ExportFlowProps) {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const [format, setFormat] = useState<ExportFormat>('pdf');
  const [destination, setDestination] = useState<ExportDestination>(() => defaultDestination(artifactId));
  const [phase, setPhase] = useState<ExportFlowPhase>('open');
  const [error, setError] = useState<string | null>(null);
  const [webViewLink, setWebViewLink] = useState<string | null>(null);
  const [hasTemplateLibrary, setHasTemplateLibrary] = useState<boolean | null>(null);

  // Reset the flow's OWN transient state (never the caller's persisted
  // family/accent — those live in the parent's useTemplatePreference) every
  // time the flow (re)opens, so a prior export's "done"/"error" phase never
  // bleeds into the next open — matches GapAddressPanel's own per-mount
  // phase reset via its jobId/gap.id effect dependency.
  useEffect(() => {
    if (!open) return;
    setPhase('open');
    setError(null);
    setWebViewLink(null);
    setDestination(defaultDestination(artifactId));
  }, [open, artifactId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getBillingEntitlement()
      .then((r) => {
        if (!cancelled) setHasTemplateLibrary(r.capabilities.includes('template_library'));
      })
      .catch(() => {
        if (!cancelled) setHasTemplateLibrary(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Escape closes; Tab/Shift+Tab wraps within the panel's own focusable set
  // — verbatim SideNavDrawer.tsx pattern (dependency-free focus trap).
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') trapFocus(e, panelRef.current);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (open) closeButtonRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const meta = orderedTemplateMeta();
  const selectedMeta = RESUME_TEMPLATE_META_BY_FAMILY[family as ResumeTemplateFamily];
  const acceptsAccent = selectedMeta ? selectedMeta.acceptsAccent : true;
  const filename = buildPrintDocumentTitle({
    displayName,
    artifact: artifactLabel,
    roleTitle,
    company,
  });
  const filenameWithExt = `${filename}.${format}`;
  const driveAvailable = driveDestinationAvailable(artifactId);

  function handleSelectFamily(next: ResumeTemplateFamily) {
    onSelectFamily(next);
    // useTemplatePreference's own setFamily already fires the PATCH
    // optimistically — this guard only prevents this component from ever
    // calling it a SECOND time for the identical value (e.g. a re-render
    // echo), matching shouldPersistSelection's documented contract.
    void shouldPersistSelection(next, family);
  }

  function handleSelectAccent(next: string) {
    onSelectAccent(next);
    void shouldPersistSelection(next, accentKey);
  }

  async function handleExport() {
    if (!isBuiltFamily(family)) return;
    setPhase('exporting');
    setError(null);
    try {
      if (destination === 'drive') {
        if (!artifactId) throw new Error('Nothing built yet to save to Drive.');
        const status = await driveStatus();
        if (!status.connected) {
          const { url } = await driveAuthorizeUrl(window.location.pathname);
          window.location.href = url;
          return;
        }
        const result = await driveExport(artifactType, artifactId, family, accentKey);
        setWebViewLink(result.webViewLink);
        setPhase('done');
        return;
      }
      if (format === 'docx') {
        await downloadResumeDocx(doc, filename, isBuiltFamily(family) ? family : undefined, accentKey);
      } else {
        const { downloadResumePdf } = await import('../../lib/resumeTemplatePdf');
        await downloadResumePdf(doc, family, filename, accentKey);
      }
      setPhase('done');
    } catch (err) {
      const apiErr = err as ApiError;
      const classified: ClassifiedDriveError | null = destination === 'drive' ? classifyDriveError(apiErr?.body) : null;
      setError(classified?.message ?? (err instanceof Error ? err.message : String(err)));
      setPhase('error');
    }
  }

  const busy = isExportFlowBusy(phase);
  const needsUpgrade =
    phase === 'error' && destination === 'drive' && isPremiumFamily(family) && hasTemplateLibrary === false;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label="Export résumé" onClick={onClose}>
      <div ref={panelRef} className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.headerRow}>
          <span className={styles.title}>Export your résumé</span>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {phase === 'done' ? (
          <DoneState destination={destination} webViewLink={webViewLink} onClose={onClose} />
        ) : (
          <>
            <div className={styles.label}>Pick a look — I'll show your OWN story in each one.</div>
            <div className={styles.cardRow} role="listbox" aria-label="Résumé template">
              {meta.map((m) => {
                const built = isBuiltFamily(m.family);
                const selected = built && family === m.family;
                const premium = isPremiumFamily(m.family);
                const locked = premium && hasTemplateLibrary === false;

                if (!built) {
                  return (
                    <div key={m.family} className={styles.stubCard} aria-disabled="true">
                      <span className={styles.comingChip}>Coming soon</span>
                      <div className={styles.cardName}>{m.displayName}</div>
                    </div>
                  );
                }

                return (
                  <button
                    key={m.family}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={selected ? styles.cardSelected : styles.card}
                    onClick={() => handleSelectFamily(m.family)}
                    disabled={busy}
                  >
                    <div className={styles.cardHeadRow}>
                      <span className={styles.cardName}>{m.displayName}</span>
                      {premium ? (
                        <span className={locked ? styles.premiumChipLocked : styles.premiumChip}>Premium</span>
                      ) : (
                        <span className={styles.freeChip}>Free</span>
                      )}
                    </div>
                    <div className={styles.cardFor}>{m.forWhom}</div>
                  </button>
                );
              })}
            </div>

            {hasTemplateLibrary === false ? (
              <div className={styles.upgradeNote} role="status">
                <span>
                  Every look is here to try — exporting a Premium one needs your trial. Your résumé stays exactly as
                  true either way.
                </span>
                <Link to="/settings" className={styles.upgradeLink} onClick={onClose}>
                  Start your trial
                </Link>
              </div>
            ) : null}

            <div className={styles.rowLabel}>Pick the dark that feels like you.</div>
            <div className={styles.swatchRow} role="listbox" aria-label="Résumé accent colour">
              {RESUME_ACCENT_OPTIONS.map((opt) => {
                const disabled = !acceptsAccent || busy;
                const selected = accentKey === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    role="option"
                    aria-selected={selected && !disabled}
                    className={selected && !disabled ? styles.swatchSelected : styles.swatch}
                    style={{ ['--swatch-hex' as string]: opt.hex }}
                    onClick={() => handleSelectAccent(opt.key)}
                    disabled={disabled}
                    title={!acceptsAccent ? 'This look stays monochrome by design' : `${opt.label} — ${opt.character}`}
                  >
                    <span className={styles.swatchDot} />
                    <span className={styles.swatchLabel}>{opt.label}</span>
                  </button>
                );
              })}
            </div>
            {!acceptsAccent ? <div className={styles.rowNote}>This look stays monochrome by design.</div> : null}

            <div className={styles.rowLabel}>Format</div>
            <div className={styles.formatRow} role="radiogroup" aria-label="Export format">
              <button
                type="button"
                role="radio"
                aria-checked={format === 'pdf'}
                className={format === 'pdf' ? styles.formatOptionSelected : styles.formatOption}
                onClick={() => setFormat('pdf')}
                disabled={busy || destination === 'drive'}
              >
                PDF
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={format === 'docx'}
                className={format === 'docx' ? styles.formatOptionSelected : styles.formatOption}
                onClick={() => setFormat('docx')}
                disabled={busy || destination === 'drive'}
              >
                .docx
              </button>
              <span className={styles.rowNote}>
                {pageSizeLabel(doc.formatVariant)} — set by this résumé's locale, not chosen here.
              </span>
            </div>

            <div className={styles.rowLabel}>Send it to</div>
            <div className={styles.formatRow} role="radiogroup" aria-label="Export destination">
              <button
                type="button"
                role="radio"
                aria-checked={destination === 'download'}
                className={destination === 'download' ? styles.formatOptionSelected : styles.formatOption}
                onClick={() => setDestination('download')}
                disabled={busy}
              >
                Download
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={destination === 'drive'}
                className={destination === 'drive' ? styles.formatOptionSelected : styles.formatOption}
                onClick={() => setDestination('drive')}
                disabled={busy || !driveAvailable}
                title={driveAvailable ? undefined : 'Build this résumé first, then Save to Drive.'}
              >
                Save to Drive
              </button>
            </div>

            <div className={styles.filenamePreview}>
              <span className={styles.filenameLabel}>Filename</span>
              <span className={styles.filenameValue}>{destination === 'drive' ? filename : filenameWithExt}</span>
            </div>

            {error ? (
              <div className={styles.errorNote} role="alert">
                {error}
              </div>
            ) : null}

            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleExport}
                disabled={busy || !isBuiltFamily(family) || (destination === 'drive' && !driveAvailable)}
              >
                {busy
                  ? destination === 'drive'
                    ? 'Saving…'
                    : 'Preparing…'
                  : needsUpgrade
                    ? 'Go to billing'
                    : destination === 'drive'
                      ? 'Save to Drive'
                      : `Download ${format === 'pdf' ? 'PDF' : '.docx'}`}
              </button>
              <button type="button" className={styles.secondaryButton} onClick={onClose} disabled={busy}>
                Cancel
              </button>
            </div>
            {needsUpgrade ? (
              <button type="button" className={styles.upgradeLink} onClick={() => navigate('/settings')}>
                Go to Settings to start your trial
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function DoneState({
  destination,
  webViewLink,
  onClose,
}: {
  destination: ExportDestination;
  webViewLink: string | null;
  onClose: () => void;
}) {
  return (
    <>
      <div className={styles.doneNote}>
        {destination === 'drive'
          ? 'Saved to your Google Drive.'
          : 'Downloaded — check your downloads for the file.'}
      </div>
      {destination === 'drive' && webViewLink ? (
        <button
          type="button"
          className={styles.primaryButton}
          onClick={() => window.open(webViewLink, '_blank', 'noopener,noreferrer')}
        >
          Open in Drive
        </button>
      ) : null}
      <button type="button" className={styles.secondaryButton} onClick={onClose}>
        Done
      </button>
    </>
  );
}

/** US Letter for us_standard, A4 for every other served locale — a READ-ONLY
 *  echo of pageGeometryForVariant's own documented rule (packages/schema's
 *  resume_templates_registry.ts), never a control (page size is a property
 *  of the document's OWN formatVariant, not a user export choice). */
function pageSizeLabel(formatVariant: string): 'US Letter' | 'A4' {
  return formatVariant === 'us_standard' ? 'US Letter' : 'A4';
}

/** Minimal, dependency-free focus trap — verbatim SideNavDrawer.tsx pattern
 *  (see that file's own doc comment): on Tab/Shift+Tab, if focus would leave
 *  the panel's own focusable set, wrap it back to the other end instead. */
function trapFocus(e: KeyboardEvent, container: HTMLElement | null) {
  if (!container) return;
  const focusable = container.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const activeEl = document.activeElement;

  if (e.shiftKey && activeEl === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && activeEl === last) {
    e.preventDefault();
    first.focus();
  }
}

export { DEFAULT_ACCENT_KEY };
